import * as d3 from 'd3';

// --- Constants ---
const TICK_STEP = 100;
const DESIRED_TICK_COUNT = 5;
const TOOLTIP_RADIUS = 4;
const Y_AXIS_CLASS = 'y-axis grid';
const X_AXIS_CLASS = 'x-axis';

// --- Utility Functions ---
function getEvenlySpacedTicks(min, max, count = DESIRED_TICK_COUNT, stepBase = TICK_STEP) {
  if (max <= min) return [min];
  const roundedMax = Math.ceil(max / stepBase) * stepBase;
  let step = (roundedMax - min) / (count - 1);
  step = Math.max(Math.round(step / stepBase) * stepBase, stepBase);
  const ticks = [];
  for (let i = 0; i < count; i++) {
    ticks.push(min + i * step);
  }
  ticks[0] = min;
  if (ticks[ticks.length - 1] < roundedMax) {
    ticks[ticks.length - 1] = roundedMax;
  }
  return ticks;
}

function ensureTooltip() {
  let tooltip = d3.select('body').select('.tooltip');
  if (tooltip.empty()) {
    tooltip = d3.select('body').append('div')
      .attr('class', 'tooltip')
      .style('position', 'absolute')
      .style('background', '#ffffff')
      .style('padding', '5px 10px')
      .style('border', '1px solid #d3d3d3')
      .style('border-radius', '3px')
      .style('pointer-events', 'none')
      .style('opacity', 0);
  }
  return tooltip;
}

// --- Main Chart Function ---
export default function (props, container, isMobile) {
  const { initialWidth, initialHeight } = props;
  const margins = {
    top: 50, right: 15, bottom: 65, left: 35,
  };
  const width = initialWidth - margins.left - margins.right;
  const height = initialHeight - margins.top - margins.bottom;

  // Clear previous chart
  container.selectAll('*').remove();

  // Set up SVG and chart group
  const svg = container.append('svg')
    .attr('width', initialWidth)
    .attr('height', initialHeight);
  const chartG = svg.append('g')
    .attr('transform', `translate(${margins.left},${margins.top})`);

  // Axes groups
  chartG.append('g').attr('class', X_AXIS_CLASS).attr('transform', `translate(0,${height})`);
  chartG.append('g').attr('class', Y_AXIS_CLASS);

  // Tooltip
  const tooltip = ensureTooltip();

  // --- Data loading and processing ---
  d3.json('https://ix.cnn.io/data/russian-drones/russian-drones-extended.json')
    .then((json) => {
      // Parse and clean data
      const data = json.data.map(d => ({
        date: d3.timeParse('%Y-%m-%d')(d.date),
        launched: +d.launched,
        roll_avg: d.roll_avg,
      })).filter(d => d.date && !isNaN(d.launched));

      // We only want data to start from Oct. 29 for the line chart
      const startDate = d3.timeParse('%Y-%m-%d')('2022-10-29');
      const lineData = data.filter(d => d.date >= startDate);

      // --- DOM: Subhed and Note ---
      // Subhed
      const biggestNumber = d3.max(data, d => d.launched);
      const maxDatum = data.slice().reverse().find(d => d.launched === biggestNumber);
      const maxDate = maxDatum ? d3.timeFormat('%B %-d')(maxDatum.date) : null;
      const subhedHtml = `<span style="font-weight:300;">The largest attack recorded in a single night this year was <b style="font-weight:500;">${biggestNumber}</b> launches on ${maxDate}, which surpassed last year\'s single-night record of 810 launches.</span><br><br><b style="color: white; background: #c52622; padding: 1px 4px; ">Daily</b><b> drone launches at Ukraine by Russia, with <span style="border-bottom: 1.5px dashed black;">30-day rolling average</span></b>`;
      d3.select('#dynamic-sentence').remove();
      container.insert('div', ':first-child')
        .attr('id', 'dynamic-sentence')
        .style('font-size', '15px')
        // .attr('class', 'subhed dek')
        .style('margin', '0 0 15px 0')
        .html(subhedHtml);

      // Note (in footer, above source/credit)
      const latestDate = d3.max(data, d => d.date);
      const latestDateStr = d3.timeFormat('%B %-d, %Y')(latestDate);
      const noteHtml = `Last updated on ${latestDateStr}. Data updated weekly.<br><br>Note: Data includes Shahed-136/131, Lancet and Kub kamikaze drones. Many drone attacks are carried out overnight for hours-long periods – these have been attributed to the day after.`;
      d3.select('#footer #dynamic-note').remove();
      d3.select('#footer')
        .insert('p', ':first-child')
        .attr('id', 'dynamic-note')
        .attr('class', 'source')
        .style('margin', '15px 0 0 0')
        .style('color', '#8C8C8C')
        .style('font-size', '12px')
        .html(noteHtml);
      if (window.pymChild) window.pymChild.sendHeight();

      // --- Chart Scales ---
      const x = d3.scaleBand()
        .domain(data.map(d => d.date))
        .range([0, width])
        .padding(0.1);
      const yMax = biggestNumber;
      const yMin = 0;
      const y = d3.scaleLinear()
        .domain([yMin, yMax])
        .range([height, 0]);

      // --- Draw Axes ---
      const monthStarts = d3.timeMonth.range(
        d3.timeMonth.floor(d3.min(data, d => d.date)),
        d3.timeMonth.ceil(d3.max(data, d => d.date)),
      );

      const spacedTicks = monthStarts.filter((_, i) => (isMobile ? i % 5 === 0 : i % 3 === 0));

      const xAxis = d3.axisBottom(x)
        // .tickValues(x.domain().filter((d, i) => {
        //   const interval = isMobile ? Math.ceil(data.length / 6) : Math.ceil(data.length / 12);
        //   return i % interval === 0;
        // }))
        .tickValues(spacedTicks)
        .tickFormat((d) => {
          // Custom format: Full for March, April, June, July; May no period; others abbreviated with period
          const monthNum = d.getMonth();
          const day = d.getDate();
          const year = d.getFullYear();
          let monthStr;
          switch (monthNum) {
            case 2: monthStr = 'March'; break;
            case 3: monthStr = 'April'; break;
            case 4: monthStr = 'May'; break;
            case 5: monthStr = 'June'; break;
            case 6: monthStr = 'July'; break;
            default:
              monthStr = `${d3.timeFormat('%b')(d)}.`;
          }
          // May should not have a period
          if (monthNum === 4) return `May ${year}`;
          return `${monthStr} ${year}`;
        });
      chartG.select('.x-axis').call(xAxis)
        .selectAll('text')
        .attr('transform', 'rotate(-45)')
        .style('text-anchor', 'end');

      const yTicks = getEvenlySpacedTicks(yMin, yMax);
      const yAxis = d3.axisLeft(y)
        .tickValues(yTicks)
        .tickSize(-width);
      chartG.select('.y-axis').call(yAxis);
      chartG.select('.y-axis').selectAll('.tick line')
        .attr('stroke', '#ccc');
      chartG.select('.y-axis').select('.domain')
        .attr('display', 'none')
        .attr('stroke', 'none');

      // --- Draw Bars (modern D3 pattern) ---
      chartG.selectAll('.bar')
        .data(data)
        .join('rect')
        .attr('class', 'bar')
        .attr('x', d => x(d.date))
        .attr('y', d => y(d.launched))
        .attr('width', x.bandwidth())
        .attr('height', d => height - y(d.launched))
        .attr('fill', '#c52622')
        .on('mouseover', function (event, d) {
          d3.select(this).attr('fill', '#6c100fff');
          // Format: Jan. 5, 2025 (no leading zero)
          const month = d3.timeFormat('%b')(d.date);
          const day = d.date.getDate();
          const year = d.date.getFullYear();
          const tooltipDate = `${month}. ${day}, ${year}`;
          tooltip.style('opacity', 1)
            .html(`<b>${tooltipDate}</b><br>Launched: <b>${d.launched}</b><br>30-day rolling avg: <b>${d.roll_avg}</b>`);
          // Tooltip positioning logic
          const tooltipNode = tooltip.node();
          const tooltipWidth = tooltipNode.offsetWidth;
          const pageWidth = window.innerWidth;
          let left = event.pageX + 15;
          if (left + tooltipWidth > pageWidth - 10) {
            left = event.pageX - tooltipWidth - 15;
          }
          if (left < 0) left = 5;
          tooltip.style('left', `${left}px`).style('top', `${event.pageY - 28}px`);
        })
        .on('mouseout', function () {
          d3.select(this).attr('fill', '#c52622');
          tooltip.style('opacity', 0);
        });

      const line = d3.line()
        .x(d => x(d.date))
        .y(d => y(d.roll_avg));

      chartG.append('path')
        .datum(lineData)
        .attr('class', 'line')
        .attr('fill', 'none')
        .attr('stroke', '#000000')
        .attr('stroke-width', isMobile ? 0.75 : 1.3)
        .attr('stroke-dasharray', '4')
        .attr('d', line);
    })
    .catch((error) => {
      console.error('Error loading or processing data:', error);
    });
}
